import React from "react";
import {useState, useContext, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import {Resolution} from "../../../logic/API";
import copyIcon from "../../../images/copy.svg";
import {
  TopicDescription,
  TopicDescriptionMqttTransfer,
  TopicDescriptionMqttTransferResponse,
} from "../../../logic/APITypes";
import {TextInput, ModalContext} from "@spg/component_library";
import {copyTextToClipboard} from "../../../logic/clipboard";

/**
 * Controls the transfer section when creating, viewing and editing topic descriptions with MQTT transfer type.
 */
function MqttTransferSection(props: {
  alternative: "create" | "view" | "edit";
  initialTopic: Resolution<TopicDescription> | undefined;
  creationResponse?: TopicDescriptionMqttTransferResponse;
  onCloseModal: () => void;
}) {
  const intl = useIntl();
  const modalContext = useContext(ModalContext);

  const [infoCopied, setInfoCopied] = useState<
    ("globalG" | "success" | "failure")[]
  >(Array(4).fill("globalG"));

  const [infoCopiedTimeout, setInfoCopiedTimeout] = useState<
    ReturnType<typeof setTimeout>[]
  >(Array(4));

  const initialTopic = props.initialTopic?.payload;
  const placeholder = props.initialTopic?.unresolved
    ? intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.placeholder.loading",
      })
    : undefined;

  useEffect(() => {
    if (props.creationResponse)
      modalContext.showModal({
        idBase: "topicDescriptionMqttInfo",
        headline: intl.formatMessage({
          id: "topicDescriptionCreation.modal.mqttInfo.headline",
        }),
        body: (
          <div className="topicDescriptionModalBody">
            <FormattedMessage
              id="topicDescriptionCreation.modal.mqttInfo.text"
              values={{b: (chunks) => <b>{chunks}</b>}}
            />
          </div>
        ),
        primaryButton: {
          name: intl.formatMessage({
            id: "topicDescriptionCreation.modal.mqttInfo.close",
          }),
          onClick: props.onCloseModal,
        },
        additionalContent: (
          <>
            <div className="topicDescriptionCreationInfoModalWrapper">
              {[
                ["connectionString", props.creationResponse.connection_string],
                ["mqttTopic", props.creationResponse.topic],
                ["username", props.creationResponse.authentication.username],
                ["password", props.creationResponse.authentication.password],
              ].map(([name, value], i) => {
                const uppercaseName = name[0].toUpperCase() + name.slice(1);
                return (
                  <TextInput
                    key={i}
                    idBase={"topicDescriptionMqttInfoModal" + uppercaseName}
                    label={intl.formatMessage({
                      id: "topicDescriptionCreation.modal.mqttInfo." + name,
                    })}
                    value={value}
                    setValue={() => undefined}
                    isReadOnly
                    trailingIconButton={{
                      iconProps: {
                        title: intl.formatMessage({
                          id:
                            "topicDescriptionCreation.modal.mqttInfo.copy" +
                            uppercaseName,
                        }),
                        href: copyIcon + "#globalG",
                        strokeColor: "#101223",
                      },
                      idBase:
                        "topicDescriptionMqttInfoModalCopy" + uppercaseName,
                      onClick: () => {
                        if (!props.creationResponse) return;
                        copyTextToClipboard(value).then(
                          () => {
                            clearTimeout(infoCopiedTimeout[i]);
                            setInfoCopied((oldInfo) => {
                              const newInfo = [...oldInfo];
                              newInfo[i] = "success";
                              return newInfo;
                            });
                            const timerId = setTimeout(() => {
                              setInfoCopied((oldInfo) => {
                                const newInfo = [...oldInfo];
                                newInfo[i] = "globalG";
                                return newInfo;
                              });
                            }, 1000);
                            setInfoCopiedTimeout((oldTimeout) => {
                              const newTimeout = [...oldTimeout];
                              newTimeout[i] = timerId;
                              return newTimeout;
                            });
                          },
                          () => {
                            clearTimeout(infoCopiedTimeout[i]);
                            setInfoCopied((oldInfo) => {
                              const newInfo = [...oldInfo];
                              newInfo[i] = "failure";
                              return newInfo;
                            });
                            const timerId = setTimeout(() => {
                              setInfoCopied((oldInfo) => {
                                const newInfo = [...oldInfo];
                                newInfo[i] = "globalG";
                                return newInfo;
                              });
                            }, 1000);
                            setInfoCopiedTimeout((oldTimeout) => {
                              const newTimeout = [...oldTimeout];
                              newTimeout[i] = timerId;
                              return newTimeout;
                            });
                          },
                        );
                      },
                    }}
                  />
                );
              })}
            </div>
            <div className="topicDescriptionModalHint">
              <FormattedMessage
                id="topicDescriptionCreation.modal.mqttInfo.hint"
                values={{b: (chunks) => <b>{chunks}</b>}}
              />
            </div>
          </>
        ),
      });
  }, [props.creationResponse]);

  useEffect(
    () => () => {
      for (const timeout of infoCopiedTimeout)
        if (timeout) clearTimeout(timeout);
    },
    [],
  );
  useEffect(() => {
    ["ConnectionString", "MqttTopic", "Username", "Password"].forEach(
      (name, index) => {
        const embeddedIcon = document.querySelector(
          `#topicDescriptionMqttInfoModalCopy${name}IconButton > svg > use`,
        );
        if (!embeddedIcon) return;
        (embeddedIcon as SVGUseElement).setAttribute(
          "href",
          copyIcon + "#" + infoCopied[index],
        );
      },
    );
  }, [infoCopied]);

  return (
    <>
      {props.alternative === "create" && (
        <>
          <div className="topicDescriptionCreationMqttInfo">
            <FormattedMessage id="topicDescriptionCreation.mqtt.information.generate" />
          </div>
          <div className="topicDescriptionCreationMqttWarning">
            <FormattedMessage id="topicDescriptionCreation.mqtt.information.access" />
          </div>
        </>
      )}
      {props.alternative !== "create" && (
        <div className="topicDescriptionCreationSpecificsContainer">
          <TextInput
            label={intl.formatMessage({
              id: "topicDescriptionCreation.modal.mqttInfo.connectionString",
            })}
            placeholder={placeholder || ""}
            idBase={"topicDescriptionCreationConnectionString"}
            value={
              (
                initialTopic?.transfer as
                  | TopicDescriptionMqttTransfer
                  | undefined
              )?.connection_string || ""
            }
            setValue={() => null}
            isReadOnly
          />
          <TextInput
            label={intl.formatMessage({
              id: "topicDescriptionCreation.modal.mqttInfo.mqttTopic",
            })}
            placeholder={placeholder || ""}
            idBase={"topicDescriptionCreationMqttTopic"}
            value={
              (
                initialTopic?.transfer as
                  | TopicDescriptionMqttTransfer
                  | undefined
              )?.topic || ""
            }
            setValue={() => null}
            isReadOnly
          />
          <div className="topicDescriptionCreationMqttInaccessible">
            <FormattedMessage id="topicDescriptionCreation.mqtt.information.inaccessible" />
          </div>
        </div>
      )}
    </>
  );
}

export {MqttTransferSection};
